import React, { useEffect, useState } from "react";
import { PrimaryButton } from "../UI";
import { useUserContext } from "../../contexts/UserContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SuccessAlert from "../UI/Alert/SuccessAlert";
import Label from "../UI/Label";
import Loader from "../UI/Loader/Loader";
import { Field, Form, Formik } from "formik";
import { AddActivityNameTaskSchema } from "../../schemas";
import Dropdown from "../UI/Dropdown/Dropdown";
import { getSavedTasks, saveTask, updateTask } from "../../utils/APIs";
import { useContentfulContext } from "../../contexts/ContentfulContext";
import Tooltip from "../UI/Tooltip";
import InlineTooltip from "../UI/Tooltip/InlineTooltip";

const ValidateActivityName = () => {
    const { userData } = useUserContext();
    const { content, enums } = useContentfulContext();

    const { pathname } = useLocation();
    const { saved_task_id } = useParams()
    const navigate = useNavigate()

    const [showSuccess, setShowSuccess] = useState("");
    const [showError, setShowError] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState();
    const [filterType, setFilterType] = useState(enums?.recurringFunctionFilterTypes?.contain?.id);

    useEffect(() => {
        if(saved_task_id) getSavedTasksDataById()
    }, [])
    
    const getSavedTasksDataById = async () => {
        try {
            setLoading(true);
            const result = await getSavedTasks(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                saved_task_id
            );
            setData(result.data[0]);
            setFilterType(result.data[0]?.filter_type || enums?.recurringFunctionFilterTypes?.contain?.id);
        } catch (error) {
            console.log("error getSavedTasksDataById: ", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            setShowError("");
            let result;
            if(saved_task_id) {
                const data = {
                    time_interval: values?.time_interval,
                    status: values?.status,
                    regex: values?.regex,
                    filter_value: values?.filter_value,
                    filter_type: filterType,
                    recipient_emails: values?.recipient_emails,
                    adobe_io_integration_id: values?.adobe_io_integration_id
                };
                result = await updateTask(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    { id: saved_task_id, data }
                );
            } else {
                const data = {
                    feature_id: content.products.adobe_target.id,
                    saved_task_name: values.saved_task_name,
                    function_id: pathname.split('/').pop(),
                    adobe_io_integration_id: values?.adobe_io_integration_id,
                    time_interval: values.time_interval,
                    status: values.status,
                    regex: values?.regex,
                    filter_value: values?.filter_value,
                    filter_type: filterType,
                    recipient_emails: values?.recipient_emails
                };
                result = await saveTask(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    data
                );
            }
            setShowSuccess(result.message);
            navigate("/functions/saved-tasks")
        } catch (error) {
            setShowError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    }

    // Transform Adobe IO integrations for dropdown
    const adobeIoOptions = userData?.adobeIoIntegrations?.map(integration => ({
        value: integration._id,
        name: integration.integration_name
    })) || [];

  return (
    <>
      <div className="w-full max-w-screen px-4 md:px-8 my-6">
        {loading && <Loader />}
        {showSuccess && (
          <SuccessAlert disappearTime={content.VALIDATE_ACTIVITY_NAME_CONTENT.timeout}>
            {content.VALIDATE_ACTIVITY_NAME_CONTENT.success}
          </SuccessAlert>
        )}
        <div className="flex flex-col lg:w-1/2 justify-center items-start">
          <div className="space-y-4 md:space-y-6 w-full">
            <Formik
                enableReinitialize
                initialValues={{
                    saved_task_name: data?.saved_task_name ? data?.saved_task_name : "",
                    time_interval: data?.time_interval || "",
                    status: data?.status || "",
                    email: "",
                    filter_value: data?.filter_value || "",
                    recipient_emails: data?.recipient_emails || [],
                    adobe_io_integration_id: data?.adobe_io_integration_id?._id || ""
                }}
                validationSchema={() => AddActivityNameTaskSchema(content.VALIDATE_NAME_CONTENT)}
                onSubmit={handleSubmit}
            >
                {({ errors, touched, setFieldValue, values }) => (
                    <Form id="fill-validate-activity-name-form">
                        <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">
                            {content.VALIDATE_ACTIVITY_NAME_CONTENT.title}
                        </h2>
                        <p>{content.VALIDATE_ACTIVITY_NAME_CONTENT.description}</p>
                       <div className="flex flex-col md:flex-row gap-9 mb-4">
                            <div className="w-full" id="tour-saved_task_name">
                                <div className="relative">
                                    <Field
                                        type="text"
                                        id="saved_task_name"
                                        name="saved_task_name"
                                        className="block px-2.5 pb-2.5 pt-2.5 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                        placeholder=" "
                                        disabled={saved_task_id}
                                    />
                                    <Label labelFor="saved_task_name">{content.VALIDATE_ACTIVITY_NAME_CONTENT.formLabels.saved_task_name}</Label>
                                    <Tooltip content={content.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.saved_task_name} class_name="-right-6 top-1/2" />
                                </div>
                                {errors.saved_task_name && touched.saved_task_name && (
                                    <p className="mt-2 text-sm text-red-5">{errors.saved_task_name}</p>
                                )}
                            </div>

                            <div className="w-full" id="tour-adobe_io_integration_id">
                                <div className="relative">
                                    <Dropdown
                                        id="tour-adobe_io_integration_id-"
                                        width={100}
                                        isWidthInPercentage={true}
                                        initialValue={adobeIoOptions.find(x => x.value === values.adobe_io_integration_id)}
                                        items={adobeIoOptions}
                                        callback={(val) => setFieldValue("adobe_io_integration_id", val.value)}
                                    />
                                    <Label labelFor="adobe_io_integration_id">{"Adobe IO Integration"}</Label>
                                    <Tooltip
                                        content={"Select the Adobe IO integration to use for this task"}
                                        class_name="-right-6 top-1/2"
                                    />
                                </div>
                                {errors.adobe_io_integration_id && touched.adobe_io_integration_id && (
                                    <p className="mt-2 text-sm text-red-5">{errors.adobe_io_integration_id}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row gap-8 mb-4">
                            <div className="w-full" id="tour-time_interval">
                                <div className="flex items-center gap-3">
                                    <div className="relative w-full">
                                        <Dropdown 
                                            id="tour-time_interval-"
                                            width={99}
                                            isWidthInPercentage={true}
                                            initialValue={content.CRON_TIME_INTERVAL.find(x => x.value == values.time_interval)}
                                            items={content.CRON_TIME_INTERVAL}
                                            callback={(val) => setFieldValue("time_interval", val.value)}
                                        />
                                        <Label labelFor="time_interval">{content.VALIDATE_ACTIVITY_NAME_CONTENT.formLabels.time_interval}</Label>
                                        <Tooltip
                                            content={content.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.time_interval}
                                            class_name="-right-6 top-1/2"
                                        />
                                    </div>
                                </div>
                                {errors.time_interval && touched.time_interval && (
                                    <p className="mt-2 text-sm text-red-5">{errors.time_interval}</p>
                                )}
                            </div>

                            <div className="w-full" id="tour-status">
                                <div className="flex items-center gap-3">
                                    <div className="relative w-full">
                                        <Dropdown 
                                            id="tour-status-"
                                            width={100}
                                            isWidthInPercentage={true}
                                            initialValue={content.LAMBDA_STATUS_VALUES.find(x => x.value == values.status)}
                                            items={content.LAMBDA_STATUS_VALUES}
                                            callback={(val) => setFieldValue("status", val.value)}
                                        />
                                        <Label labelFor="status">{content.VALIDATE_ACTIVITY_NAME_CONTENT.formLabels.status}</Label>
                                        <Tooltip
                                            content={content.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.status}
                                            class_name="-right-6 top-1/2"
                                        />
                                    </div>
                                </div>
                                {errors.status && touched.status && (
                                    <p className="mt-2 text-sm text-red-5">{errors.status}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col flex-wrap justify-center gap-1" id="tour-filter_type">
                            <div className="flex items-center content-center gap-3">
                                <label className="text-sm font-medium block">{"Filter Type"}</label>
                                <InlineTooltip content={content.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.filter_type} />
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {Object.values(enums?.recurringFunctionFilterTypes)?.map((x, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => setFilterType(x.id)}
                                        className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${filterType == x.id ? "bg-primary text-white": "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                                    >
                                        {x.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="my-4" id="tour-filter_value">
                            <div className="relative">
                                <Field
                                    type="text"
                                    id="filter_value"
                                    name="filter_value"
                                    className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                    placeholder=" "
                                />
                                <Label labelFor="filter_value">{enums?.recurringFunctionFilterTypes?.[filterType].name}</Label>
                                <Tooltip content={content.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.filter_value} class_name="-right-8 top-1/2" />
                            </div>
                            {errors.filter_value && touched.filter_value ? (
                            <p className="mt-2 text-sm text-red-5">{errors.regex}</p>
                            ) : null}
                        </div>
                        <div className="my-4" id="tour-email_input">
                            <div className="relative">
                                <Field
                                    type="text"
                                    id="email"
                                    name="email"
                                    className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                    placeholder=" "
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if(!errors.email) {
                                                console.log(e.target.value)
                                                setFieldValue('recipient_emails', [...values.recipient_emails, e.target.value]);
                                                setFieldValue('email', '');
                                            }
                                        }
                                    }}
                                />
                                <Label labelFor="email">{content.VALIDATE_ACTIVITY_NAME_CONTENT.formLabels.email}</Label>
                                <Tooltip content={content.VALIDATE_ACTIVITY_NAME_CONTENT.help_text.recipient_emails} class_name="-right-8 top-1/2" />
                            </div>
                            <p className="mt-2 text-sm ">{content.VALIDATE_ACTIVITY_NAME_CONTENT.note}</p>
                            {errors.email && touched.email ? (
                                <p className="mt-2 text-sm text-red-5">{errors.email}</p>
                            ) : null}
                            {!errors.email && errors.recipient_emails && touched.recipient_emails ? (
                            <   p className="mt-2 text-sm text-red-5">{errors.recipient_emails}</p>
                            ) : null}
                            {values.recipient_emails.length >0  && <ul className="max-h-[200px] max-w-[450px] overflow-y-auto my-4 border border-gray-2 rounded-lg ">
                                {values.recipient_emails.map((x, i) => (
                                    <li key={i} className="p-3 flex justify-between w-full border-b border-gray-2">
                                        {x}
                                        <button
                                            type="button"
                                            className="text-red-5"
                                            onClick={() => {
                                                const newRecipientEmails = [...values.recipient_emails];
                                                newRecipientEmails.splice(i, 1);
                                                setFieldValue('recipient_emails', newRecipientEmails);
                                            }}
                                        >x</button>
                                    </li>
                                ))}
                            </ul>}
                        </div>
                        {showError && <p className="my-2 text-sm text-red-5">{showError}</p>}
                        <div className="flex justify-end mt-6">
                            <PrimaryButton type="submit">
                                {content.VALIDATE_ACTIVITY_NAME_CONTENT.submit_cta}
                            </PrimaryButton>
                        </div>

                    </Form>
                )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default ValidateActivityName;
